/**
 * Cache Layer Service
 * Provides in-memory caching to reduce localStorage access and improve performance
 */

// In-memory cache storage
const memoryCache = new Map();
const cacheStats = {
  hits: 0,
  misses: 0,
  sets: 0,
  deletes: 0,
  size: 0
};

// Cache configuration
const CACHE_CONFIG = {
  MAX_SIZE: 1000, // Maximum number of items in cache
  TTL: 5 * 60 * 1000, // 5 minutes default TTL
  CLEANUP_INTERVAL: 2 * 60 * 1000, // 2 minutes cleanup interval
  ENABLE_STATS: true
};

/**
 * Cache item structure
 */
class CacheItem {
  constructor(value, ttl = CACHE_CONFIG.TTL) {
    this.value = value;
    this.timestamp = Date.now();
    this.ttl = ttl;
    this.accessCount = 0;
  }

  isExpired() {
    return Date.now() - this.timestamp > this.ttl;
  }

  touch() {
    this.accessCount++;
    this.timestamp = Date.now();
  }
}

/**
 * Get value from cache
 * @param {string} key - Cache key
 * @returns {any} Cached value or undefined
 */
export const cacheGet = (key) => {
  if (!key) return undefined;
  
  const item = memoryCache.get(key);
  
  if (!item) {
    if (CACHE_CONFIG.ENABLE_STATS) cacheStats.misses++;
    return undefined;
  }
  
  if (item.isExpired()) {
    memoryCache.delete(key);
    if (CACHE_CONFIG.ENABLE_STATS) {
      cacheStats.misses++;
      cacheStats.size--;
    }
    return undefined;
  }
  
  item.touch();
  if (CACHE_CONFIG.ENABLE_STATS) cacheStats.hits++;
  return item.value;
};

/**
 * Set value in cache
 * @param {string} key - Cache key
 * @param {any} value - Value to cache
 * @param {number} ttl - Time to live in milliseconds
 */
export const cacheSet = (key, value, ttl = CACHE_CONFIG.TTL) => {
  if (!key) return false;
  
  // Check cache size limit
  if (memoryCache.size >= CACHE_CONFIG.MAX_SIZE) {
    evictLeastRecentlyUsed();
  }
  
  const item = new CacheItem(value, ttl);
  memoryCache.set(key, item);
  
  if (CACHE_CONFIG.ENABLE_STATS) {
    cacheStats.sets++;
    cacheStats.size = memoryCache.size;
  }
  
  return true;
};

/**
 * Delete value from cache
 * @param {string} key - Cache key
 * @returns {boolean} True if key was deleted
 */
export const cacheDelete = (key) => {
  if (!key) return false;
  
  const deleted = memoryCache.delete(key);
  
  if (deleted && CACHE_CONFIG.ENABLE_STATS) {
    cacheStats.deletes++;
    cacheStats.size = memoryCache.size;
  }
  
  return deleted;
};

/**
 * Clear all cache entries
 */
export const cacheClear = () => {
  memoryCache.clear();
  if (CACHE_CONFIG.ENABLE_STATS) {
    cacheStats.size = 0;
  }
};

/**
 * Check if key exists in cache
 * @param {string} key - Cache key
 * @returns {boolean} True if key exists and is not expired
 */
export const cacheHas = (key) => {
  if (!key) return false;
  
  const item = memoryCache.get(key);
  if (!item) return false;
  
  if (item.isExpired()) {
    memoryCache.delete(key);
    if (CACHE_CONFIG.ENABLE_STATS) cacheStats.size--;
    return false;
  }
  
  return true;
};

/**
 * Get cache statistics
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  const total = cacheStats.hits + cacheStats.misses;
  const hitRate = total > 0 ? (cacheStats.hits / total) * 100 : 0;
  
  return {
    ...cacheStats,
    hitRate: hitRate.toFixed(2) + '%',
    totalRequests: total,
    memoryUsage: memoryCache.size
  };
};

/**
 * Evict least recently used items
 */
const evictLeastRecentlyUsed = () => {
  const entries = Array.from(memoryCache.entries());
  
  // Sort by access count and timestamp
  entries.sort((a, b) => {
    const [keyA, itemA] = a;
    const [keyB, itemB] = b;
    
    if (itemA.accessCount !== itemB.accessCount) {
      return itemA.accessCount - itemB.accessCount;
    }
    
    return itemA.timestamp - itemB.timestamp;
  });
  
  // Remove 20% of least used items
  const toRemove = Math.ceil(entries.length * 0.2);
  
  for (let i = 0; i < toRemove; i++) {
    const [key] = entries[i];
    memoryCache.delete(key);
  }
  
  if (CACHE_CONFIG.ENABLE_STATS) {
    cacheStats.size = memoryCache.size;
  }
};

/**
 * Cleanup expired items
 */
export const cleanupExpiredItems = () => {
  const now = Date.now();
  let cleaned = 0;
  
  for (const [key, item] of memoryCache.entries()) {
    if (item.isExpired()) {
      memoryCache.delete(key);
      cleaned++;
    }
  }
  
  if (CACHE_CONFIG.ENABLE_STATS) {
    cacheStats.size = memoryCache.size;
  }
  
  return cleaned;
};

/**
 * Get all cache keys
 * @returns {Array<string>} Array of cache keys
 */
export const getCacheKeys = () => {
  return Array.from(memoryCache.keys());
};

/**
 * Get cache size
 * @returns {number} Number of items in cache
 */
export const getCacheSize = () => {
  return memoryCache.size;
};

/**
 * Batch operations for better performance
 */
export const cacheBatchGet = (keys) => {
  const results = {};
  
  for (const key of keys) {
    results[key] = cacheGet(key);
  }
  
  return results;
};

export const cacheBatchSet = (entries, ttl = CACHE_CONFIG.TTL) => {
  const results = {};
  
  for (const [key, value] of Object.entries(entries)) {
    results[key] = cacheSet(key, value, ttl);
  }
  
  return results;
};

export const cacheBatchDelete = (keys) => {
  const results = {};
  
  for (const key of keys) {
    results[key] = cacheDelete(key);
  }
  
  return results;
};

/**
 * Cache with automatic refresh
 * @param {string} key - Cache key
 * @param {Function} fetcher - Function to fetch data if not in cache
 * @param {number} ttl - Time to live
 * @returns {Promise<any>} Cached or fresh data
 */
export const cacheGetOrSet = async (key, fetcher, ttl = CACHE_CONFIG.TTL) => {
  const cached = cacheGet(key);
  
  if (cached !== undefined) {
    return cached;
  }
  
  try {
    const value = await fetcher();
    cacheSet(key, value, ttl);
    return value;
  } catch (error) {
    console.error(`Cache fetch error for key ${key}:`, error);
    throw error;
  }
};

/**
 * Initialize cache cleanup interval
 */
let cleanupInterval = null;

export const startCacheCleanup = () => {
  if (cleanupInterval) return;
  
  cleanupInterval = setInterval(() => {
    cleanupExpiredItems();
  }, CACHE_CONFIG.CLEANUP_INTERVAL);
};

export const stopCacheCleanup = () => {
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
  }
};

/**
 * Cache warming - preload frequently accessed data
 * @param {Array<{key: string, fetcher: Function, ttl?: number}>} items - Items to preload
 */
export const warmCache = async (items) => {
  const promises = items.map(async ({ key, fetcher, ttl = CACHE_CONFIG.TTL }) => {
    try {
      const value = await fetcher();
      cacheSet(key, value, ttl);
      return { key, success: true };
    } catch (error) {
      console.error(`Cache warming failed for key ${key}:`, error);
      return { key, success: false, error };
    }
  });
  
  return Promise.all(promises);
};

/**
 * Cache performance monitoring
 */
export const getCachePerformance = () => {
  const stats = getCacheStats();
  const now = Date.now();
  
  return {
    ...stats,
    averageAccessTime: stats.totalRequests > 0 ? 
      (now - (memoryCache.size * 1000)) / stats.totalRequests : 0,
    memoryEfficiency: (memoryCache.size / CACHE_CONFIG.MAX_SIZE) * 100,
    lastCleanup: now
  };
};

// Auto-start cleanup
if (typeof window !== 'undefined') {
  startCacheCleanup();
}

export default {
  cacheGet,
  cacheSet,
  cacheDelete,
  cacheClear,
  cacheHas,
  getCacheStats,
  cleanupExpiredItems,
  getCacheKeys,
  getCacheSize,
  cacheBatchGet,
  cacheBatchSet,
  cacheBatchDelete,
  cacheGetOrSet,
  startCacheCleanup,
  stopCacheCleanup,
  warmCache,
  getCachePerformance
};



